import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { FlagDE, FlagFR, FlagGB, FlagIT } from "./flags";

export type LanguageCode = "en" | "de" | "fr" | "it";

/** Each language named in itself, so a user can find theirs whatever the UI language is. */
export const LANGUAGES: { code: LanguageCode; name: string; Flag: FC<{ title?: string }> }[] = [
  { code: "en", name: "English", Flag: FlagGB },
  { code: "de", name: "Deutsch", Flag: FlagDE },
  { code: "fr", name: "Français", Flag: FlagFR },
  { code: "it", name: "Italiano", Flag: FlagIT },
];

const LanguageLabel = ({ code }: { code: string }): ReactNode => {
  const language = LANGUAGES.find((l) => l.code === code);
  if (!language) return code;
  const { Flag, name } = language;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Flag />
      {name}
    </Box>
  );
};

export interface LanguageSelectProps {
  value: LanguageCode;
  onChange: (language: LanguageCode) => void;
  /** Accessible name of the select, e.g. the translated "System language". */
  label: string;
  disabled?: boolean;
  /** Render the options inside the parent instead of a portal, e.g. inside a menu that closes on click-away. */
  disablePortal?: boolean;
  /**
   * `outlined` (default): a form field. `inline`: no border or underline, sized to its content, for a
   * value that sits at the end of a menu row.
   */
  variant?: "outlined" | "inline";
}

/** A language dropdown that shows each language's flag next to its name. */
const LanguageSelect: FC<LanguageSelectProps> = ({
  value,
  onChange,
  label,
  disabled,
  disablePortal,
  variant = "outlined",
}) => (
  <Select
    size="small"
    {...(variant === "inline"
      ? {
          variant: "standard" as const,
          disableUnderline: true,
          sx: { font: "inherit" },
          // The small standard input pads 1px above and 5px below its text, which lifts the flag and the
          // name above the row's label: drop that padding and centre them. Inline, because MUI sets the
          // padding with a tripled class selector.
          SelectDisplayProps: {
            style: { display: "flex", alignItems: "center", paddingTop: 0, paddingBottom: 0 },
          },
        }
      : { fullWidth: true })}
    value={value}
    disabled={disabled}
    inputProps={{ "aria-label": label }}
    MenuProps={{ disablePortal }}
    renderValue={(code) => <LanguageLabel code={code} />}
    onChange={(event: SelectChangeEvent<LanguageCode>) => {
      const next = event.target.value as LanguageCode;
      if (next !== value) onChange(next);
    }}
  >
    {LANGUAGES.map(({ code }) => (
      <MenuItem key={code} value={code}>
        <LanguageLabel code={code} />
      </MenuItem>
    ))}
  </Select>
);

export default LanguageSelect;
